import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Heart, SquaresFour } from '@/core/ui/icons';
import type { ReactNode } from 'react';
import { usePageDescription } from '@/core/page';
import { cn, EmptyState, ErrorState, Skeleton } from '@/core/ui';
import { useOptionalPermissions } from '@/modules/identity/public';
import { useFavorites } from '../application/favorites';
import { useProduct, useProducts } from '../application/queries';
import { describeProductsPage } from '../domain/describe';
import { BRANDS, type Brand } from '../domain/product';
import { ProductCard } from './ProductCard';
import { ProductDetailPanel } from './ProductDetailPanel';
import { PromoBanner } from './PromoBanner';

export interface ProductsView {
  brand?: Brand;
  favorites?: boolean;
  /** Slug do produto aberto no painel lateral. */
  product?: string;
}

/**
 * Catálogo de produtos: menu de marcas à esquerda, vitrine à direita.
 * Ocupa a área inteira da página — o menu secundário vai até o rodapé.
 */
export function ProductsPage({ view }: { view: ProductsView }) {
  const { user } = useOptionalPermissions();
  const navigate = useNavigate();
  const closeDetail = () =>
    void navigate({ to: '/catalogo', search: (prev) => ({ ...prev, produto: undefined }) });
  // Visitante não tem favoritos: o coração leva ao login e volta para cá.
  const favorites = useFavorites(user?.id ?? 'visitante');
  const href = useRouterState({ select: (s) => s.location.href });
  const toggleFavorite = (id: string) =>
    user ? favorites.toggle(id) : void navigate({ to: '/login', search: { redirect: href } });
  const products = useProducts({
    brand: view.brand,
    favoriteIds: view.favorites ? favorites.ids : undefined,
  });
  // Mesma lista em cache: o produto do painel sem requisição a mais.
  const open = useProduct(view.product);
  usePageDescription(() =>
    describeProductsPage({
      brand: view.brand,
      favorites: view.favorites,
      products: products.data ?? [],
      open: open.data,
    }),
  );

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col md:flex-row">
      <aside className="shrink-0 bg-surface/40 px-4 py-6 backdrop-blur-xl md:w-56">
        <h1 className="px-2 text-xl font-semibold">Produtos</h1>

        <nav aria-label="Filtros do catálogo" className="mt-5">
          <div className="flex gap-2 md:flex-col">
            <Pill
              active={!view.brand && !view.favorites}
              search={{}}
              icon={<SquaresFour className="size-3.5" />}
            >
              Início
            </Pill>
            {user && (
              <Pill
                active={!!view.favorites}
                search={{ favoritos: true }}
                icon={<Heart className="size-3.5" />}
              >
                Favoritos
                {favorites.ids.length > 0 && (
                  <span className="ml-auto text-fg-subtle">{favorites.ids.length}</span>
                )}
              </Pill>
            )}
          </div>

          <ul className="-mx-4 mt-6 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:mt-8 md:flex-col md:gap-1 md:px-0">
            {BRANDS.map((b) => (
              <li key={b.id} className="shrink-0">
                <Link
                  to="/catalogo"
                  search={{ marca: b.id }}
                  className={cn(
                    'block rounded-xl px-3 py-2 text-sm font-medium transition-colors',
                    view.brand === b.id
                      ? 'bg-brand-400/15 text-brand-300'
                      : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
                  )}
                >
                  {b.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <div className="@container min-w-0 flex-1 space-y-8 px-4 py-6 sm:px-6 lg:px-8">
        <PromoBanner />

        {products.isPending ? (
          <Grid>
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className="aspect-[4/3] rounded-lg" />
            ))}
          </Grid>
        ) : products.isError ? (
          <ErrorState error={products.error} onRetry={() => void products.refetch()} />
        ) : products.data.length === 0 ? (
          <EmptyState
            title={view.favorites ? 'Nenhum favorito ainda' : 'Nenhum produto nesta marca'}
            description={view.favorites ? 'Toque no coração de um produto para guardá-lo aqui.' : undefined}
          />
        ) : (
          <Grid>
            {products.data.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                favorite={favorites.isFavorite(p.id)}
                selected={p.slug === view.product}
                onToggleFavorite={() => toggleFavorite(p.id)}
              />
            ))}
          </Grid>
        )}
      </div>

      {view.product && (
        <ProductDetailPanel
          slug={view.product}
          isFavorite={favorites.isFavorite}
          onToggleFavorite={toggleFavorite}
          onClose={closeDetail}
        />
      )}
    </div>
  );
}

function Grid({ children }: { children: ReactNode }) {
  return (
    // Colunas pelo espaço do container, não da tela: com o painel aberto, a
    // vitrine encolhe e perde uma coluna em vez de espremer os cartões.
    <div className="grid grid-cols-2 gap-x-4 gap-y-8 @3xl:grid-cols-3 @5xl:grid-cols-4 @7xl:grid-cols-5">
      {children}
    </div>
  );
}

function Pill({
  active,
  search,
  icon,
  children,
}: {
  active: boolean;
  search: { favoritos?: boolean };
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Link
      to="/catalogo"
      search={search}
      className={cn(
        'flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium transition-colors',
        active ? 'bg-surface-3 text-fg' : 'bg-surface-2 text-fg-muted hover:bg-surface-3 hover:text-fg',
      )}
    >
      {icon}
      {children}
    </Link>
  );
}
